import eventemitter3 from 'eventemitter3';

import ERRORS from './Errors';


const noOp = (): void => undefined;

type Context = any;
type Data = any;

type EmitParams = {
  event: {
    name: string,
    args: any[],
  },
  successEvent: string,
  errorEvent: string,
  timeout: number,
  predicate?: (context: Context) => any,
};
type EmitResult = Promise<Data> & { cancel?: () => void };

/**
 * Generic message buss for the frontend application.
 * Integrates with node/backend via socket connection send/receive messages
 *
 * @class MessagingService
 * @extends {eventemitter3}
 */
export class MessagingService extends eventemitter3<string> {
  events = {};

  /**
   * Creates an instance of MessagingService.
   */
  constructor() {
    super();
    this.events = {};
    window.v2MessagingService = this;
  }

  /**
   * Emits a custom event with a payload
   *
   * @param {string} eventName - name of the event
   * @param {any} payload - data to be transferred through the message bus
   */
  emit(eventName: string, ...payload: any[]) {
    const [firstArgument] = payload;
    if (!eventName || eventName.length === 0) {
      throw new Error(ERRORS.INVALID_EVENT);
    }
    // used by external scripts, ex streaming analytics or DataScience team
    window.dispatchEvent(
      new CustomEvent('Cam4InternalEvents', { detail: { name: eventName, details: firstArgument } })
    );

    return super.emit(eventName, ...payload);
  }

  /**
   * @param name the name of the event to trigger
   * @param args the arguments of the triggered event
   * @param successEvent the name of the success event
   * @param errorEvent the name of the error event
   * @param timeout timeout in milliseconds
   * @param predicate optional - predicate to apply in order to match request-response events
   */
  emitAndWait = ({
    event: { name: eventName, args },
    successEvent,
    errorEvent,
    timeout,
    predicate,
  }: EmitParams): EmitResult => {
    if (!timeout) {
      throw new Error('invalid timeout');
    }
    if (!eventName || !successEvent || !errorEvent) {
      throw new Error('invalid parameters');
    }
    if (typeof predicate !== 'undefined' && typeof predicate !== 'function') {
      throw new Error('invalid predicate');
    }

    const predicateMatches = (ctx: Context) => {
      try {
        return Boolean(predicate(ctx));
      } catch (e) {
        return false;
      }
    };

    let eventArgs: any[] = [];
    if (Array.isArray(args)) {
      eventArgs = args;
    } else if (typeof args !== 'undefined') {
      eventArgs = [args];
    }
    let cancelFn = undefined;

    const result: EmitResult = new Promise<Data>((resolve, reject) => {
      const canceledError = new Error('canceled');
      const timeoutError = new Error('timeout');
      let isCanceled = false;
      let timer: ReturnType<typeof setTimeout> = null;
      let cleanup = noOp;

      const onSuccess = (data: Data, context: Context): void => {
        if (isCanceled) {
          return;
        }
        if (predicate && !predicateMatches(context)) {
          return;
        }
        cleanup();
        resolve(data);
      };

      const onError = (data: Data, context: Context): void => {
        if (isCanceled) {
          return;
        }
        if (predicate && !predicateMatches(context)) {
          return;
        }
        cleanup();
        reject(data);
      };

      const onCancelOrTimeout = (data: Data): void => {
        isCanceled = true;
        cleanup();
        reject(data);
      };

      cleanup = (): void => {
        clearTimeout(timer);
        this.off(successEvent, onSuccess);
        this.off(errorEvent, onError);
      };

      cancelFn = (): void => {
        onCancelOrTimeout(canceledError);
      };

      this.on(successEvent, onSuccess);
      this.on(errorEvent, onError);
      this.emit(eventName, ...eventArgs);
      timer = setTimeout(() => onCancelOrTimeout(timeoutError), timeout);
    });

    result.cancel = cancelFn;

    return result;
  };
}

let instance: MessagingService = null;

declare global {
  interface Window {
    v2MessagingService: MessagingService;
  }
}

const getInstance = (singleton = true): MessagingService => {
  if (singleton) {
    if (!instance) {
      instance = new MessagingService();
    }

    return instance;
  }

  return new MessagingService();
};

export default getInstance;
